import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Smartphone, Play, Apple } from 'lucide-react';

const AppPreviewSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // App features showcase
  const screenshots = [
    {
      id: 1,
      title: "Metro Route Finder",
      description: "Find optimal metro routes between any two locations via stations",
      image: "/images/route.PNG",
      color: "#3D5AFE"
    },
    {
      id: 2,
      title: "Live Navigation",
      description: "Real-time journey tracking with smart alerts for interchanges and exits",
      image: "/images/live_journey.PNG",
      color: "#00F5D4"
    },
    {
      id: 3,
      title: "Route Details & Planning",
      description: "Comprehensive metro route information with journey planning",
      image: "/images/route_planning.PNG",
      color: "#9D4EDD"
    },
    {
      id: 4,
      title: "Nearby Metro Stations",
      description: "Discover metro stations near your location with real-time updates",
      image: "/images/nearby.PNG",
      color: "#FF6B35"
    },
    {
      id: 5,
      title: "Live Journey Sharing",
      description: "Share your metro journey with friends and family in real-time",
      image: "/images/tracking.PNG",
      color: "#4ECDC4"
    },
    {
      id: 6,
      title: "Disruption Alerts",
      description: "Stay informed with instant alerts about metro disruptions and delays",
      image: "/images/alert.PNG",
      color: "#E74C3C"
    },
    {
      id: 7,
      title: "Multi-Language Support",
      description: "Regional language support for seamless metro navigation experience",
      image: "/images/language.PNG",
      color: "#8E44AD"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % screenshots.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + screenshots.length) % screenshots.length);
  };

  return (
    <section className="app-preview-section section">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">App Preview</h2>
          <p className="section-subtitle">
            Experience the future of metro navigation through our intuitive interface
          </p>
        </motion.div>

        <div className="preview-container">
          {/* Mobile Mockup Frame */}
          <div className="mobile-mockup">
            <div className="mockup-frame">
              <div className="mockup-notch"></div>
              <div className="mockup-screen">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentSlide}
                    className="screenshot-container"
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5 }}
                  >
                    <img
                      src={screenshots[currentSlide].image}
                      alt={screenshots[currentSlide].title}
                      className="screenshot"
                    />
                    <div className="screenshot-overlay">
                      <h3 className="screenshot-title">{screenshots[currentSlide].title}</h3>
                      <p className="screenshot-description">{screenshots[currentSlide].description}</p>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Screenshot Info */}
          <div className="screenshot-info">
            <motion.div
              className="info-content"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="info-title">{screenshots[currentSlide].title}</h3>
              <p className="info-description">{screenshots[currentSlide].description}</p>
              
              {/* Feature Highlights */}
              <div className="feature-highlights">
                <div className="highlight-item">
                  <div className="highlight-icon">🚇</div>
                  <span>Metro Routes</span>
                </div>
                <div className="highlight-item">
                  <div className="highlight-icon">📍</div>
                  <span>Live Tracking</span>
                </div>
                <div className="highlight-item">
                  <div className="highlight-icon">🔔</div>
                  <span>Smart Alerts</span>
                </div>
                <div className="highlight-item">
                  <div className="highlight-icon">🌐</div>
                  <span>Multi-Language</span>
                </div>
              </div>

              {/* Navigation Dots */}
              <div className="navigation-dots">
                {screenshots.map((_, index) => (
                  <button
                    key={index}
                    className={`dot ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => setCurrentSlide(index)}
                    style={{
                      backgroundColor: index === currentSlide ? screenshots[currentSlide].color : '#2a2d35'
                    }}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Navigation Arrows */}
        <div className="carousel-navigation">
          <motion.button
            className="nav-button prev"
            onClick={prevSlide}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ChevronLeft />
          </motion.button>
          
          <motion.button
            className="nav-button next"
            onClick={nextSlide}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ChevronRight />
          </motion.button>
        </div>

        {/* Download CTA */}
        <motion.div
          className="download-cta"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="cta-content">
            <div className="cta-text-section">
              <Smartphone className="cta-icon" />
              <h3>Ready to Transform Your Metro Experience?</h3>
              <p>Download MetroRahi today and join millions of smart travelers</p>
                                     <div className="cta-buttons-small">
                         <a 
                           href="https://play.google.com/store/apps/details?id=com.resiara.metrorahi" 
                           target="_blank" 
                           rel="noopener noreferrer"
                           className="cta-button-small play-store"
                         >
                           <Play className="button-icon" />
                           <span>Play Store</span>
                         </a>
                         <a 
                           href="https://apps.apple.com/in/app/metrorahi/id6752489811" 
                           target="_blank" 
                           rel="noopener noreferrer"
                           className="cta-button-small app-store"
                         >
                           <Apple className="button-icon" />
                           <span>App Store</span>
                         </a>
                       </div>
            </div>
            <div className="cta-qr-section">
              <div className="qr-code-container">
                <div className="qr-code">
                  <div className="qr-pattern">
                    {[...Array(25)].map((_, i) => (
                      <div
                        key={i}
                        className="qr-cell"
                        style={{
                          backgroundColor: Math.random() > 0.3 ? '#000' : '#fff'
                        }}
                      />
                    ))}
                  </div>
                </div>
                <p className="qr-label">Scan to download</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AppPreviewSection;
